import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {ObjectBaseComponent} from '../../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../../core/shared/service/message/mvs-message.service';
import {ConfirmationService} from 'primeng/api';
import {ObserverService} from '../../../../../../core/shared/object/service/observer.service';
import {NavigationItem} from '../../../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../../core/shared/filter/api/filter.criteria';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from '../../../../../../core/shared/helper/widget-function-call-back-create';

/**
 * CustomerFormType Object Page component.
 *
 * Renders a Formulartyp (Customer Form Type) as a Complex Configuration Entity with left-navigation
 * sections for each of its same-module child entities:
 *  - Felder (CustomerFormTypeField — FK: typeDtoId)
 *  - Sub-Formulare (CustomerFormType self-reference — FK: parentDtoId)
 *  - Interaktionstypen (CustomerInteractionType — FK: customerFormTypeDtoId)
 *  - Onboarding-Typen (CustomerOnboardingType — FK: questionnaireFormTypeDtoId)
 *
 * Each child widget is FK-defaulted via WidgetFunctionCallBackCreate.widgetToolbarCreate so that
 * the parent reference is preselected when creating a new child row.
 *
 * Referenced gold-standard pattern: DmDocumentTypeObjectComponent / TicketTypeObjectComponent.
 */
@Component({
    selector: 'customer-form-type-object-component',
    templateUrl: './customer-form-type-object.component.html',
    standalone: false
})
export class CustomerFormTypeObjectComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    // Child widgets per nav section
    fieldsWidget: WidgetData;
    subFormTypesWidget: WidgetData;
    interactionTypesWidget: WidgetData;
    onboardingTypesWidget: WidgetData;

    // Object header widget
    widgetObjectHeader: WidgetData;

    navigationItems: NavigationItem[] = [
        {
            label: 'Felder',
            action: 'fields',
            tooltip: 'Formularfelder dieses Formulartyps',
            icon: 'fa-regular fa-list',
            toggleable: false,
            default: true
        },
        {
            label: 'Sub-Formulare',
            action: 'subForms',
            tooltip: 'Untergeordnete Formulartypen',
            icon: 'fa-regular fa-sitemap',
            toggleable: false,
            default: false
        },
        {
            label: 'Interaktionstypen',
            action: 'interactionTypes',
            tooltip: 'Kundeninteraktionstypen, die diesen Formulartyp verwenden',
            icon: 'fa-regular fa-comments',
            toggleable: false,
            default: false
        },
        {
            label: 'Onboarding-Typen',
            action: 'onboardingTypes',
            tooltip: 'Onboarding-Typen, die diesen Formulartyp als Fragebogen verwenden',
            icon: 'fa-regular fa-clipboard-list-check',
            toggleable: false,
            default: false
        }
    ];

    private queryParamSubscription: Subscription;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected observerService: ObserverService,
        private route: ActivatedRoute) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit() {
        super.ngOnInit();

        // Restore active nav item from query param (e.g. back-navigation)
        this.queryParamSubscription = this.route.queryParams.subscribe(params => {
            const navItem = params['navItem'];
            if (navItem) {
                const found = this.navigationItems.find(item => item.action === navItem);
                if (found) {
                    this.activeNavigationItems = found;
                }
            }
        });
    }

    /**
     * Called after the CustomerFormType DTO is loaded.
     * Sets tab title, creates object header, emits nav items, and creates child widgets.
     */
    onObjectChanged() {
        const title = `${this.dto?.['name'] ?? 'Formulartyp'} - Konfiguration`;
        this.setPageTitle(title);

        this.widgetObjectHeader = WidgetFactory.createWidgetObjectHeaderQl(
            'cr.customer.form.type.object.header',
            'Formulartyp Header',
            'cr.CustomerFormType',
            this.dto.id,
            [{type: 'entity', name: 'formGroup', as: 'fg', joinType: 'left'}]
        );

        if (!this.activeNavigationItems) {
            this.activeNavigationItems = this.navigationItems[0];
        }
        this.onNavigationItems.emit(this.navigationItems);

        this.createChildWidgets();

        // Breadcrumbs handled by mvs-object-navigation-bread-crumb
        this.onBreadcrumbItems.emit([]);
    }

    /**
     * Build all child-section widgets with FK defaults so that create-new pre-selects the parent id.
     */
    private createChildWidgets(): void {
        const formTypeId = this.objectIdentifier.objectId;

        // --- Felder (CustomerFormTypeField, FK: typeDtoId)
        this.fieldsWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cr.customer.form.type.object.fields',
            'Felder',
            'cr.CustomerFormTypeField',
            [FilterCriteria.create('e.typeId', FilterCriteria.cOperatorEqual, formTypeId)]
        );
        const fieldsDefaults: WidgetToolbarCreateInterface[] = [
            {fieldName: 'typeDtoId', fieldValue: formTypeId}
        ];
        this.fieldsWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fieldsDefaults);

        // --- Sub-Formulare (CustomerFormType self-reference, FK: parentDtoId)
        this.subFormTypesWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cr.customer.form.type.object.subForms',
            'Sub-Formulare',
            'cr.CustomerFormType',
            [FilterCriteria.create('e.parentId', FilterCriteria.cOperatorEqual, formTypeId)]
        );
        const subFormDefaults: WidgetToolbarCreateInterface[] = [
            {fieldName: 'parentDtoId', fieldValue: formTypeId}
        ];
        this.subFormTypesWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(subFormDefaults);

        // --- Interaktionstypen (CustomerInteractionType, FK: customerFormTypeDtoId)
        this.interactionTypesWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cr.customer.form.type.object.interactionTypes',
            'Interaktionstypen',
            'cr.CustomerInteractionType',
            [FilterCriteria.create('e.customerFormTypeId', FilterCriteria.cOperatorEqual, formTypeId)]
        );
        const interactionDefaults: WidgetToolbarCreateInterface[] = [
            {fieldName: 'customerFormTypeDtoId', fieldValue: formTypeId}
        ];
        this.interactionTypesWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(interactionDefaults);

        // --- Onboarding-Typen (CustomerOnboardingType, FK: questionnaireFormTypeDtoId)
        this.onboardingTypesWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'cr.customer.form.type.object.onboardingTypes',
            'Onboarding-Typen',
            'cr.CustomerOnboardingType',
            [FilterCriteria.create('e.questionnaireFormTypeId', FilterCriteria.cOperatorEqual, formTypeId)]
        );
        const onboardingDefaults: WidgetToolbarCreateInterface[] = [
            {fieldName: 'questionnaireFormTypeDtoId', fieldValue: formTypeId}
        ];
        this.onboardingTypesWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(onboardingDefaults);
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.queryParamSubscription) {
            this.queryParamSubscription.unsubscribe();
        }
    }
}
